import {
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	makeStyles,
} from "@material-ui/core";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import { ClearIcon } from "@mui/x-date-pickers";
import React, { useMemo, useState } from "react";
import { ModalTreeFilterControl, TreeNode } from "./dialogFilter";

type TreePickerControlProps = {
	onSelect: (
		value: number,
		description: string,
		findNode: any,
		showPath?: boolean,
		listAssetDrawings?: number[],
		multipleSelectNodes?: boolean,
		onHide?: (showAssetTree?: boolean) => void,
		showAssetTree?: boolean,
	) => void;
	isLoading?: boolean;
	setListAssetDrawings?: Function;
	onSuccess?: (success: boolean) => void;
	showClearButton?: boolean;
	showFullPath?: boolean;
	disabled?: boolean;
	dataSource: TreeNode;
	[key: string]: any;
};

const useStyles = makeStyles((theme) => ({
	root: {
		width: "100%",
		backgroundColor: theme.palette.background.paper,
	},
	paper: {
		width: "100%",
		maxHeight: 600,
	},
}));

function findNameById(node: TreeNode, id: number): string | null {
	if (node?.id === id) {
		return node.name;
	}
	if (node?.children) {
		for (const child of node.children) {
			const result = findNameById(child, id);
			if (result !== null) {
				return result;
			}
		}
	}
	return null;
}

function getNodePath(
	node: TreeNode,
	nodeId: number | string,
	parentPath: string = "",
	isRoot: boolean = true,
): string | null {
	if (node.id === nodeId) {
		if (isRoot) {
			return "";
		}
		return parentPath ? `${parentPath}/${node.name}` : node.name;
	}

	if (node.children && node.children.length > 0) {
		for (const child of node.children) {
			const currentPath = isRoot
				? ""
				: parentPath
					? `${parentPath}/${node.name}`
					: node.name;

			const foundPath = getNodePath(child, nodeId, currentPath, false);
			if (foundPath !== null) {
				return foundPath;
			}
		}
	}

	return null;
}

export const TreePickerControlV2 = (props: TreePickerControlProps) => {
	const classes = useStyles();
	const {
		value,
		onSelect,

		styleLabel,
		dataSource,
		getOptionName,
		inputTitle = "Asset",
		showPath = false,
		multipleSelectNodes = false,
		showClearButton = false,
		showFullPath = false,
		showAssetTree,
		onHide,
		listAssetDrawings = [],
		onSuccess,
		isLoading,
		disabled = false,
		...other
	}: TreePickerControlProps = props;

	const [open, setOpen] = useState(false);

	const handleClickListItem = (event: any) => {
		event.stopPropagation();
		if (disabled) return;
		setOpen(true);
	};

	const handleClose = (newValue: any, newDescription: any, findNode: any) => {
		setOpen(false);
		if (newValue) {
			if (onSelect !== undefined) {
				onSelect(newValue, newDescription, findNode);
			}
		}
	};

	const description = useMemo(
		() => findNameById(dataSource, value) || "",
		[dataSource, value],
	);

	const fullPath = useMemo(
		() => getNodePath(dataSource, value) || "",
		[dataSource, value],
	);

	return (
		<Grid container className={classes.root}>
			{!showAssetTree ? (
				<Grid
					container
					item
					md={12}
					xs={12}
					style={{ position: "relative" }}
					title={props.description}
				>
					<Grid item style={{ position: "relative", width: "100%" }}>
						<TextField
							id="treePickerTextField"
							label={inputTitle || ""}
							variant="outlined"
							value={showFullPath ? fullPath : description}
							margin="dense"
							autoComplete="off"
							fullWidth
							disabled
							InputProps={{
								style: {
									color: disabled ? "grey" : "black",
									cursor: "pointer",
									paddingRight: 25,
								},
								endAdornment: (
									<InputAdornment
										position="end"
										style={{
											cursor: "pointer",
										}}
									>
										{showClearButton && value ? (
											<IconButton
												aria-label="clear input"
												onClick={(e) => {
													e.stopPropagation();
													onSelect(0, "", "");
												}}
												style={{
													cursor: "pointer",
													zIndex: 100,
												}}
												edge="end"
												size="small"
											>
												<ClearIcon />
											</IconButton>
										) : null}
										<AccountTreeIcon
											style={{
												cursor: "pointer",
											}}
										/>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
					<div
						style={{
							position: "absolute",
							top: 0,
							bottom: 0,
							left: 0,
							right: 0,
							cursor: "pointer",
						}}
						onClick={handleClickListItem}
					></div>
				</Grid>
			) : (
				<div></div>
			)}
			<Grid item xs={12} md={12}>
				<ModalTreeFilterControl
					classes={{
						paper: classes.paper,
					}}
					onHide={onHide}
					id="modal-treeview-filter"
					title={props.title}
					keepMounted
					open={open}
					onClose={handleClose}
					isLoading={isLoading}
					value={value}
					data={dataSource}
					onBackdropClick={() => setOpen(false)}
					multipleSelectNodes={multipleSelectNodes}
					listAssetDrawings={listAssetDrawings}
					setListAssetDrawings={listAssetDrawings}
					onSuccess={onSuccess}
					{...(other as any)}
				></ModalTreeFilterControl>
			</Grid>
		</Grid>
	);
};
